import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, query } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatAmount, methodLabel } from "../../lib/format";
import type { ListResponse, PaymentLink, PaymentMethod } from "../../lib/types";
import {
  Button,
  CopyButton,
  EmptyState,
  ErrorNotice,
  Field,
  Modal,
  PageHeader,
  Pagination,
  SelectField,
  Sheet,
  SheetHeader,
  Skeleton,
  StatusPill,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";

const PAGE_SIZE = 25;

const METHODS: PaymentMethod[] = [
  "ecocash",
  "innbucks",
  "onemoney",
  "omari",
  "smilecash",
  "ewallet",
  "card",
  "hosted",
];

/**
 * PaymentLinks is the page that makes the platform usable without a developer.
 *
 * A link is a URL the merchant sends over WhatsApp; the payer opens it, chooses how to
 * pay, and the money lands in the same balance as an API payment. Everything else on
 * this page — the counter, the collected total — exists so the merchant can tell at a
 * glance which of the links they have sent out are actually working.
 */
export function PaymentLinks() {
  const { mode } = useAuth();
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<PaymentLink | null>(null);

  const links = useQuery({
    queryKey: ["payment-links", mode, offset, status],
    queryFn: () =>
      api.get<ListResponse<PaymentLink>>(
        `/api/dashboard/payment-links${query({ mode, limit: PAGE_SIZE, offset, status })}`,
      ),
  });

  const setLinkStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: "active" | "paused" }) =>
      api.patch<PaymentLink>(`/api/dashboard/payment-links/${id}`, { status: next }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["payment-links"] }),
  });

  const rows = links.data?.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title="Payment links"
        description="Share a link and get paid. No website and no code required — send it over WhatsApp, print it as a QR code, or put it in an email."
        actions={<Button variant="primary" onClick={() => setCreating(true)}>New link</Button>}
      />

      <Sheet className="settle settle-1 overflow-hidden">
        <SheetHeader
          title="Your links"
          actions={
            <SelectField
              label=""
              aria-label="Filter by status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setOffset(0);
              }}
              options={[
                { value: "", label: "All statuses" },
                { value: "active", label: "Active" },
                { value: "paused", label: "Paused" },
                { value: "completed", label: "Completed" },
                { value: "expired", label: "Expired" },
              ]}
              className="w-40"
            />
          }
        />

        {links.isLoading ? (
          <Skeleton rows={5} />
        ) : links.error ? (
          <ErrorNotice error={links.error} className="m-5" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No payment links yet"
            description="Make one, send it to a customer, and you can take money today."
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                New link
              </Button>
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Link</Th>
                  <Th align="right">Amount</Th>
                  <Th align="right">Paid</Th>
                  <Th align="right">Collected</Th>
                  <Th>Status</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {rows.map((link) => (
                  <Tr key={link.id}>
                    <Td>
                      <span className="block font-medium text-ink-strong">{link.title}</span>
                      <code className="mt-0.5 block font-mono text-[11.5px] text-ink-faint">
                        /l/{link.slug}
                      </code>
                    </Td>
                    <Td align="right" className="tabular">
                      {link.amount
                        ? `${link.currency} ${formatAmount(link.amount.minor)}`
                        : <span className="text-ink-muted">Payer chooses</span>}
                    </Td>
                    <Td align="right" className="tabular">
                      {link.payment_count}
                      {link.single_use && <span className="text-ink-faint"> / 1</span>}
                      {link.max_payments && <span className="text-ink-faint"> / {link.max_payments}</span>}
                    </Td>
                    <Td align="right" className="tabular">
                      {link.collected
                        ? `${link.currency} ${formatAmount(link.collected.minor)}`
                        : "—"}
                    </Td>
                    <Td>
                      <StatusPill status={link.status} />
                    </Td>
                    <Td align="right">
                      <span className="flex justify-end gap-1">
                        {/* Copying the address is what this page exists for, so it is a
                            named button rather than an icon a merchant has to find. */}
                        <CopyButton value={link.url} label="Copy link" />
                        {link.status === "active" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setLinkStatus.mutate({ id: link.id, next: "paused" })}
                          >
                            Pause
                          </Button>
                        )}
                        {link.status === "paused" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setLinkStatus.mutate({ id: link.id, next: "active" })}
                          >
                            Resume
                          </Button>
                        )}
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={links.data?.total ?? 0}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Sheet>

      <NewLinkDialog
        open={creating}
        mode={mode}
        onClose={() => setCreating(false)}
        onCreated={(link) => {
          setCreating(false);
          setCreated(link);
          queryClient.invalidateQueries({ queryKey: ["payment-links"] });
        }}
      />

      <Modal
        open={created !== null}
        onClose={() => setCreated(null)}
        title="Your link is ready"
        description="Send this to your customer. They can pay without an account and without installing anything."
        footer={<Button variant="primary" onClick={() => setCreated(null)}>Done</Button>}
      >
        {created && (
          <div className="rounded-md border border-rule-strong bg-paper-sunk px-4 py-3">
            <p className="label mb-1.5">Share this address</p>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate font-mono text-[13px] text-ink-strong">
                {created.url}
              </code>
              <CopyButton value={created.url} />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

function NewLinkDialog({
  open,
  mode,
  onClose,
  onCreated,
}: {
  open: boolean;
  mode: string;
  onClose: () => void;
  onCreated: (link: PaymentLink) => void;
}) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    currency: "USD",
    amount: "",
    openAmount: false,
    minAmount: "",
    maxAmount: "",
    singleUse: false,
    collectName: false,
    collectEmail: true,
    collectMobile: true,
    collectNote: "",
    methods: ["ecocash", "innbucks", "card"] as string[],
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<PaymentLink>("/api/dashboard/payment-links", {
        mode,
        title: form.title,
        description: form.description,
        currency: form.currency,
        // An open-amount link sends no amount at all; the payer chooses on the page,
        // within the bounds below. A minimum is required, or the link could be settled
        // with one cent.
        amount_minor: form.openAmount ? null : toMinor(form.amount),
        min_amount_minor: form.openAmount ? toMinor(form.minAmount) : null,
        max_amount_minor: form.openAmount ? toMinor(form.maxAmount) : null,
        allowed_methods: form.methods,
        single_use: form.singleUse,
        collect_name: form.collectName,
        collect_email: form.collectEmail,
        collect_mobile: form.collectMobile,
        collect_note: form.collectNote,
      }),
    onSuccess: onCreated,
  });

  const toggleMethod = (method: string) =>
    setForm((f) => ({
      ...f,
      methods: f.methods.includes(method)
        ? f.methods.filter((m) => m !== method)
        : [...f.methods, method],
    }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New payment link"
      description="What are you charging for, and how much?"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
            Create link
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <ErrorNotice error={create.error} />

        <Field
          label="What is this for?"
          required
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Handwoven basket"
          hint="Your customer sees this on the payment page."
        />

        <div className="grid gap-4 sm:grid-cols-[7rem_1fr]">
          <SelectField
            label="Currency"
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
            options={[
              { value: "USD", label: "USD" },
              { value: "ZWG", label: "ZWG" },
            ]}
          />
          <Field
            label="Amount"
            inputMode="decimal"
            value={form.amount}
            disabled={form.openAmount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="45.00"
          />
        </div>

        <Checkbox
          label="Let the payer choose the amount"
          hint="For donations, deposits and part-payments."
          checked={form.openAmount}
          onChange={(checked) => setForm({ ...form, openAmount: checked })}
        />

        {form.openAmount && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Smallest they may pay"
              required
              inputMode="decimal"
              value={form.minAmount}
              onChange={(e) => setForm({ ...form, minAmount: e.target.value })}
              placeholder="5.00"
              hint="Required — without a floor the link can be settled for one cent."
            />
            <Field
              label="Largest they may pay"
              inputMode="decimal"
              value={form.maxAmount}
              onChange={(e) => setForm({ ...form, maxAmount: e.target.value })}
              placeholder="500.00"
              hint="Optional."
            />
          </div>
        )}

        <fieldset className="rounded-md border border-rule-strong px-4 py-3">
          <legend className="label px-1">How can they pay?</legend>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {METHODS.map((method) => (
              <Checkbox
                key={method}
                label={methodLabel(method)}
                checked={form.methods.includes(method)}
                onChange={() => toggleMethod(method)}
              />
            ))}
          </div>
        </fieldset>

        <fieldset className="rounded-md border border-rule-strong px-4 py-3">
          <legend className="label px-1">What should we ask them for?</legend>
          <div className="grid gap-1.5">
            <Checkbox
              label="Their name"
              checked={form.collectName}
              onChange={(checked) => setForm({ ...form, collectName: checked })}
            />
            <Checkbox
              label="Their email address"
              hint="Needed to send a receipt."
              checked={form.collectEmail}
              onChange={(checked) => setForm({ ...form, collectEmail: checked })}
            />
            <Checkbox
              label="Their mobile number"
              hint="Required by EcoCash, OneMoney, O'mari and SmileCash."
              checked={form.collectMobile}
              onChange={(checked) => setForm({ ...form, collectMobile: checked })}
            />
          </div>
        </fieldset>

        <Field
          label="Ask one more question"
          hint="Optional — for example, the pupil's name or a delivery address."
          value={form.collectNote}
          onChange={(e) => setForm({ ...form, collectNote: e.target.value })}
        />

        <Checkbox
          label="Close the link after one payment"
          hint="For a single item or a single invoice."
          checked={form.singleUse}
          onChange={(checked) => setForm({ ...form, singleUse: checked })}
        />

        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

/** Checkbox is local to the billing pages: a label, a hint, and a real checkbox that a
 *  screen reader can name. */
export function Checkbox({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 py-0.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-verdigris"
      />
      <span className="min-w-0">
        <span className="block text-[13px] text-ink">{label}</span>
        {hint && <span className="block text-[12px] text-ink-muted">{hint}</span>}
      </span>
    </label>
  );
}

/** toMinor converts a typed decimal to integer minor units without going through a
 *  float, so 10.10 does not become 1009. */
export function toMinor(value: string, exponent = 2): number | null {
  const trimmed = value.replace(/,/g, "").trim();
  if (!trimmed) return null;
  if (!/^\d*(\.\d*)?$/.test(trimmed)) return null;

  const [whole = "0", frac = ""] = trimmed.split(".");
  if (frac.length > exponent) return null;
  const padded = frac.padEnd(exponent, "0");
  const minor = Number(`${whole}${padded}`);
  return Number.isFinite(minor) ? minor : null;
}
